import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL, SETUP_2FA, GET_QR_CODE, SEND_SMS_CODE } from '../apiServices/api_routes';
import { GetUserData, GetMyWorkspaces, setAccessToken, setWorkspaceSelectionRequired } from '../redux/usersSlice';
import { takePostRegisterValidateKey } from '../utils/pendingInviteKey';
import Cookies from 'js-cookie';
import { SpinnerIos16Regular } from '@fluentui/react-icons';
import Logo from '../assets/longLogoBright.png';
import styles from '../components/Register/css/Register.module.css';
import CountryCodeSelector from '../components/CommonComponents/CountryCodeSelector';

const CompleteProfile = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        full_name: '',
        organisation: '',
        role: '',
    });
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [azureData, setAzureData] = useState(null);
    const [showSecure, setShowSecure] = useState(false);
    const [userData, setUserData] = useState(null);
    const [twoFAMethod, setTwoFAMethod] = useState('qr');
    const [smsStep, setSmsStep] = useState('phone');

    useEffect(() => {
        // Get invitation key and claims from URL parameters
        const key = searchParams.get('key');
        const claims = searchParams.get('claims');

        if (!key) {
            toast.error('Invalid registration link');
            navigate('/');
            return;
        }

        // Try to get Azure data from URL claims first (fallback)
        if (claims) {
            try {
                const decodedClaims = JSON.parse(atob(claims));
                setAzureData({
                    email: decodedClaims.email,
                    name: decodedClaims.name,
                    given_name: decodedClaims.given_name,
                    family_name: decodedClaims.family_name,
                    organization: decodedClaims.organization,
                });

                // Pre-fill form fields with Microsoft data
                setFormData({
                    full_name: decodedClaims.given_name && decodedClaims.family_name ? `${decodedClaims.given_name} ${decodedClaims.family_name}`.trim() : decodedClaims.name || '',
                    organisation: decodedClaims.organization || '',
                    role: '', // Role will be filled by user
                });
                return; // Use URL claims if available
            } catch (error) {
                console.error('Failed to decode URL claims:', error);
            }
        }

        // Fallback to backend session
        const fetchAzureData = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}accounts/auth/complete-profile/`);
                setAzureData({
                    email: response.data.email,
                    name: response.data.name,
                    given_name: response.data.given_name,
                    family_name: response.data.family_name,
                    organization: response.data.organization,
                });

                // Pre-fill form fields with Microsoft data
                setFormData({
                    full_name: response.data.given_name && response.data.family_name ? `${response.data.given_name} ${response.data.family_name}`.trim() : response.data.name || '',
                    organisation: response.data.organization || '',
                    role: '', // Role will be filled by user
                });
            } catch (error) {
                console.error('Failed to fetch Azure data:', error);
                toast.error('Failed to load registration data. Please restart registration.');
                navigate('/');
            }
        };

        fetchAzureData();
    }, [searchParams, navigate]);

    const handleChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async e => {
        e.preventDefault();

        const { full_name, organisation, role } = formData;
        if (!full_name || !organisation || !role) {
            toast.error('All fields are required');
            return;
        }

        if (!termsAccepted) {
            toast.error('You must accept the Terms and Privacy Policy');
            return;
        }

        try {
            setLoading(true);

            // Prepare request data with fallback claims
            const requestData = {
                full_name,
                organisation,
                role,
            };

            // Add claims as fallback if available from URL
            const claims = searchParams.get('claims');
            if (claims) {
                requestData.azure_claims = claims;
            }

            const response = await axios.post(`${API_BASE_URL}accounts/auth/complete-profile/`, requestData);

            if (response.data.success) {
                console.log('[DEBUG] Complete Profile Response:', response.data);
                if (response.data.requires_2fa_setup) {
                    // Show 2FA setup screen
                    console.log('[DEBUG] Setting up 2FA screen with user data:', response.data.user);
                    setUserData(response.data.user);
                    setShowSecure(true);
                    toast.success('Account created! Please set up 2FA.');
                } else {
                    dispatch(setAccessToken(response.data.access));
                    Cookies.set('refresh', response.data.refresh, {
                        secure: true,
                        sameSite: 'strict'
                    });
                    await dispatch(GetUserData());
                    toast.success('Account created successfully!');
                    const inviteKey = searchParams.get('key');
                    if (inviteKey) {
                        navigate(`/validate-invite/${inviteKey}`, { replace: true });
                        return;
                    }
                    navigate('/project');
                }
            }
        } catch (error) {
            console.error('Complete profile error:', error);
            const errorMessage = error.response?.data?.error || 'Failed to complete profile';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.body}>
            <div className={styles.dotedBg}>
                <div className={styles.formCard}>
                    <div className={styles.formHeader}>
                        <img src={Logo} width={73} alt="lens-logo" />
                    </div>
                    {showSecure ? (
                        <SecureAccount
                            invitationKey={searchParams.get('key')}
                            userData={userData}
                            data={formData}
                            setShowSecure={setShowSecure}
                            twoFAMethod={twoFAMethod}
                            setTwoFAMethod={setTwoFAMethod}
                            smsStep={smsStep}
                            setSmsStep={setSmsStep}
                        />
                    ) : (
                        <div className={styles.formBody}>
                            <h5 className={styles.formLabel}>Complete Your Profile</h5>
                            <p className={styles.formSubtitle}>Please provide the following information to complete your account setup</p>

                            <form onSubmit={handleSubmit}>
                                <div>
                                    <p className={styles.fieldLabel}>
                                        Full Name <span style={{ color: '#B10E1C' }}>*</span>
                                    </p>
                                    <input
                                        value={formData.full_name}
                                        name="full_name"
                                        onChange={handleChange}
                                        required
                                        className={'form-control ' + styles.inputField}
                                        placeholder="Enter your full name"
                                    />
                                </div>
                                <div>
                                    <p className={styles.fieldLabel}>
                                        Organisation <span style={{ color: '#B10E1C' }}>*</span>
                                    </p>
                                    <input
                                        value={formData.organisation}
                                        name="organisation"
                                        onChange={handleChange}
                                        required
                                        className={'form-control ' + styles.inputField}
                                        placeholder="Enter your organisation"
                                    />
                                </div>
                                <div>
                                    <p className={styles.fieldLabel}>
                                        Role <span style={{ color: '#B10E1C' }}>*</span>
                                    </p>
                                    <input
                                        value={formData.role}
                                        name="role"
                                        onChange={handleChange}
                                        required
                                        className={'form-control ' + styles.inputField}
                                        placeholder="Enter your role"
                                    />
                                </div>
                                <div onClick={() => setTermsAccepted(!termsAccepted)} role="button" className={styles.rememberMeSection}>
                                    <div>
                                        <input checked={termsAccepted} required type="checkbox" />
                                    </div>
                                    <label className={styles.rememberBeText}>
                                        I agree to the Lens{' '}
                                        <a href="https://www.iubenda.com/terms-and-conditions/15531073" target="_blank" rel="noreferrer">
                                            Terms
                                        </a>{' '}
                                        and acknowledge the{' '}
                                        <a href="https://www.iubenda.com/privacy-policy/15531073/legal?ifr=true&height=650&newmarkup=yes&an=no" target="_blank" rel="noreferrer">
                                            Privacy Policy.
                                        </a>{' '}
                                        for information on data usage and protection.
                                    </label>
                                </div>
                                <div>
                                    <button
                                        disabled={loading || !termsAccepted || !formData.full_name || !formData.organisation || !formData.role}
                                        className={styles.submitBtn}
                                        type="submit">
                                        {loading ? (
                                            <>
                                                <SpinnerIos16Regular className={styles.spinner} />
                                                Creating Account...
                                            </>
                                        ) : (
                                            'Create Account'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// SecureAccount component — post–2FA navigation aligned with Register (validate-invite → project when invited)
const SecureAccount = ({ invitationKey, data, setShowSecure, userData, twoFAMethod, setTwoFAMethod, smsStep, setSmsStep }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [registerLoading, setRegisterLoading] = useState(false);
    const [qrVerificationCode, setQrVerificationCode] = useState(['', '', '', '', '', '']);
    const [smsVerificationCode, setSmsVerificationCode] = useState(['', '', '', '', '', '']);
    const [smsData, setSmsData] = useState({
        code: '',
        number: '',
    });
    const [qrData, setQrData] = useState('');
    const [qrLoading, setQrLoading] = useState(false);

    // Refs for OTP inputs
    const qrInputRefs = useRef([]);
    const smsInputRefs = useRef([]);

    // Handle QR code verification input
    const handleQrCodeChange = (index, event) => {
        const newCode = [...qrVerificationCode];
        newCode[index] = event.target.value;
        setQrVerificationCode(newCode);

        // Move to the next input field
        if (event.target.value && index < 5) {
            qrInputRefs.current[index + 1].focus();
        }
    };

    // Handle QR code key down (backspace)
    const handleQrKeyDown = (index, event) => {
        // Move to the previous input field if backspace is pressed and the current input field is empty
        if (event.key === 'Backspace' && !qrVerificationCode[index] && index > 0) {
            qrInputRefs.current[index - 1].focus();
        }
    };

    // Handle QR code paste
    const handleQrPaste = event => {
        const pastedData = event.clipboardData.getData('Text');
        if (pastedData.length === 6 && qrInputRefs.current[0] === document.activeElement) {
            // If pasted data length is 6 and the first input is focused, update the OTP
            const newOTP = pastedData.split('').slice(0, 6);
            setQrVerificationCode(newOTP);
        }
    };

    // Handle SMS verification code input
    const handleSmsCodeChange = (index, event) => {
        const newCode = [...smsVerificationCode];
        newCode[index] = event.target.value;
        setSmsVerificationCode(newCode);

        // Move to the next input field
        if (event.target.value && index < 5) {
            smsInputRefs.current[index + 1].focus();
        }
    };

    // Handle SMS key down (backspace)
    const handleSmsKeyDown = (index, event) => {
        // Move to the previous input field if backspace is pressed and the current input field is empty
        if (event.key === 'Backspace' && !smsVerificationCode[index] && index > 0) {
            smsInputRefs.current[index - 1].focus();
        }
    };

    // Handle SMS paste
    const handleSmsPaste = event => {
        const pastedData = event.clipboardData.getData('Text');
        if (pastedData.length === 6 && smsInputRefs.current[0] === document.activeElement) {
            // If pasted data length is 6 and the first input is focused, update the OTP
            const newOTP = pastedData.split('').slice(0, 6);
            setSmsVerificationCode(newOTP);
        }
    };

    // Fetch QR code data
    const fetchQrCode = async () => {
        setQrLoading(true);
        try {
            const response = await axios.post(`${API_BASE_URL}${GET_QR_CODE}`, {
                email: userData?.email,
            });
            setQrData(response.data.qr_data);
        } catch (error) {
            console.error('Failed to fetch QR code:', error);
            toast.error('Failed to generate QR code. Please try again.');
        } finally {
            setQrLoading(false);
        }
    };

    // Fetch QR code when QR screen is shown
    useEffect(() => {
        if (twoFAMethod === 'qr' && !qrData) {
            fetchQrCode();
        }
    }, [twoFAMethod]);

    // Handle SMS phone number change
    const handleSmsChange = (field, value) => {
        setSmsData(prev => ({ ...prev, [field]: value }));
    };

    // Handle QR code activation
    const handleQrActivation = async () => {
        const code = qrVerificationCode.join('');
        if (code.length !== 6) {
            toast.error('Please enter a 6-digit verification code');
            return;
        }

        setRegisterLoading(true);
        try {
            const response = await axios.post(`${API_BASE_URL}${SETUP_2FA}`, {
                email: userData?.email,
                mfa_method: 'app',
                verification_code: code,
            });

            const access = response?.data?.access;
            const refresh = response?.data?.refresh;
            if (access && refresh) {
                toast.success('2FA setup completed successfully!');
                dispatch(setAccessToken(access));
                Cookies.set('refresh', refresh, { secure: true, sameSite: 'strict' });
                await dispatch(GetUserData());
                const validateKey = takePostRegisterValidateKey() || invitationKey;
                if (validateKey) {
                    navigate(`/validate-invite/${validateKey}`, { replace: true });
                    return;
                }
                const res = await dispatch(GetMyWorkspaces());
                const memberships = res?.payload?.data?.memberships || [];
                if (!memberships.length) {
                    navigate('/no-workspace-access', { replace: true });
                } else if (memberships.length === 1) {
                    dispatch(setWorkspaceSelectionRequired(false));
                    navigate('/project', { replace: true });
                } else {
                    dispatch(setWorkspaceSelectionRequired(true));
                    navigate('/select-workspace', { replace: true });
                }
            } else {
                toast.success('2FA setup completed successfully! Please sign in to continue.');
                takePostRegisterValidateKey();
                navigate(invitationKey ? `/login?invite=${encodeURIComponent(invitationKey)}` : '/login');
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.error || 'Invalid verification code. Please try again.';
            toast.error(errorMessage);
        } finally {
            setRegisterLoading(false);
        }
    };

    // Handle SMS phone number submission
    const handleSendSmsCode = async () => {
        if (!smsData.code || !smsData.number) {
            toast.error('Please enter your phone number');
            return;
        }

        setRegisterLoading(true);
        try {
            // Send SMS verification code
            const phoneNumber = smsData.code + smsData.number;
            await axios.post(`${API_BASE_URL}${SEND_SMS_CODE}`, {
                email: userData?.email,
                phone_number: phoneNumber,
            });

            toast.success('Verification code sent to your phone!');
            setSmsStep('verification');
        } catch (error) {
            const errorMessage = error?.response?.data?.error || 'Failed to send verification code. Please try again.';
            toast.error(errorMessage);
        } finally {
            setRegisterLoading(false);
        }
    };

    // Handle SMS verification code submission
    const handleSmsActivation = async () => {
        const code = smsVerificationCode.join('');
        if (code.length !== 6) {
            toast.error('Please enter a 6-digit verification code');
            return;
        }

        setRegisterLoading(true);
        try {
            const phoneNumber = smsData.code + smsData.number;
            const response = await axios.post(`${API_BASE_URL}${SETUP_2FA}`, {
                email: userData?.email,
                mfa_method: 'sms_twilio',
                phone_number: phoneNumber,
                verification_code: code,
            });

            const access = response?.data?.access;
            const refresh = response?.data?.refresh;
            if (access && refresh) {
                toast.success('2FA setup completed successfully!');
                dispatch(setAccessToken(access));
                Cookies.set('refresh', refresh, { secure: true, sameSite: 'strict' });
                await dispatch(GetUserData());
                const validateKey = takePostRegisterValidateKey() || invitationKey;
                if (validateKey) {
                    navigate(`/validate-invite/${validateKey}`, { replace: true });
                    return;
                }
                const res = await dispatch(GetMyWorkspaces());
                const memberships = res?.payload?.data?.memberships || [];
                if (!memberships.length) {
                    navigate('/no-workspace-access', { replace: true });
                } else if (memberships.length === 1) {
                    dispatch(setWorkspaceSelectionRequired(false));
                    navigate('/project', { replace: true });
                } else {
                    dispatch(setWorkspaceSelectionRequired(true));
                    navigate('/select-workspace', { replace: true });
                }
            } else {
                toast.success('2FA setup completed successfully! Please sign in to continue.');
                takePostRegisterValidateKey();
                navigate(invitationKey ? `/login?invite=${encodeURIComponent(invitationKey)}` : '/login');
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.error || 'Invalid verification code. Please try again.';
            toast.error(errorMessage);
        } finally {
            setRegisterLoading(false);
        }
    };

    // Handle back navigation
    const handleBack = () => {
        if (twoFAMethod === 'sms' && smsStep === 'verification') {
            setSmsStep('phone');
        } else if (twoFAMethod === 'sms' && smsStep === 'phone') {
            setTwoFAMethod('qr');
        } else {
            setShowSecure(false);
        }
    };

    // QR Code Screen
    if (twoFAMethod === 'qr') {
        return (
            <div className={styles.formBody}>
                <h5 className={styles.formLabel}>Set up 2FA</h5>
                <p className={styles.formSubtitle}>
                    For heightened security measures, kindly scan the provided code using your authenticator app and enter the corresponding verification code below. We will use
                    this method by default.
                </p>

                <div className="text-center">
                    <div className="d-flex justify-content-center mb-3">
                        {qrLoading ? (
                            <div
                                style={{
                                    width: '168px',
                                    height: '168px',
                                    backgroundColor: '#f8f9fa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '8px',
                                }}>
                                <SpinnerIos16Regular className="rotateLoader" />
                            </div>
                        ) : qrData ? (
                            <div
                                style={{
                                    width: '168px',
                                    height: '168px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '8px',
                                    padding: '8px',
                                }}>
                                <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`}
                                    alt="QR Code"
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                        ) : (
                            <div
                                style={{
                                    width: '168px',
                                    height: '168px',
                                    backgroundColor: '#f8f9fa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '8px',
                                }}>
                                <span className="text-muted">QR Code</span>
                            </div>
                        )}
                    </div>
                    <p className="text-muted small">Open your authenticator app and scan this QR code to add your Lens account.</p>
                </div>

                <div className="mb-3">
                    <label className={styles.fieldLabel}>Enter verification code</label>
                    <div className="d-flex justify-content-center">
                        <div className="d-flex" style={{ gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                            {[0, 1, 2, 3, 4, 5].map(index => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength="1"
                                    value={qrVerificationCode[index]}
                                    onChange={event => handleQrCodeChange(index, event)}
                                    onKeyDown={event => handleQrKeyDown(index, event)}
                                    ref={ref => (qrInputRefs.current[index] = ref)}
                                    onPaste={event => handleQrPaste(event)}
                                    className={'form-control text-center ' + styles.inputField}
                                    style={{
                                        width: '14%',
                                        height: '48px',
                                        fontSize: '18px',
                                        fontFamily: 'monospace',
                                        fontWeight: 'bold',
                                        border: 'none',
                                        borderBottom: '1px solid var(--Neutral-Gray-6)',
                                        outline: 'none',
                                        textAlign: 'center',
                                        borderRadius: '0px',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-4">
                    <button onClick={handleQrActivation} disabled={registerLoading || qrVerificationCode.join('').length !== 6} className={styles.submitBtn}>
                        Activate account {registerLoading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                    </button>
                    <span role="button" onClick={() => setTwoFAMethod('sms')} className={styles.secondaryBtnLink}>
                        Use SMS authentication
                    </span>
                </div>
            </div>
        );
    }

    // SMS Phone Number Screen
    if (twoFAMethod === 'sms' && smsStep === 'phone') {
        return (
            <div className={styles.formBody}>
                <h5 className={styles.formLabel}>Set up 2FA</h5>
                <p className={styles.formSubtitle}>
                    For heightened security measures, add your mobile number below to receive verification code. We will use this method by default. Your mobile number will only be
                    used for account security. Standard SMS charges will apply.
                </p>

                <div className="d-flex">
                    <div style={{ width: '120px' }} className="me-2">
                        <div>
                            <p className={styles.fieldLabel}>Code</p>
                            <CountryCodeSelector
                                value={smsData?.code}
                                onChange={value => handleSmsChange('code', value)}
                                placeholder="Please Select"
                                className="" // Remove problematic CSS class
                                name="code"
                                ariaLabel="Country code"
                            />
                        </div>
                    </div>
                    <div className="w-100">
                        <div>
                            <p className={styles.fieldLabel}>Mobile Number</p>
                            <input
                                type="number"
                                placeholder="Mobile Number"
                                value={smsData?.number}
                                onChange={e => handleSmsChange('number', e.target.value)}
                                className={'form-control ' + styles.inputField}
                            />
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-4">
                    <button onClick={handleSendSmsCode} disabled={!smsData?.code || !smsData?.number || registerLoading} className={styles.submitBtn}>
                        Send Code {registerLoading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                    </button>
                    <span role="button" onClick={handleBack} className={styles.secondaryBtnLink}>
                        Back
                    </span>
                </div>
            </div>
        );
    }

    // SMS Verification Code Screen
    if (twoFAMethod === 'sms' && smsStep === 'verification') {
        return (
            <div className={styles.formBody}>
                <h5 className={styles.formLabel}>Enter verification code</h5>
                <p className={styles.formSubtitle}>The verification code is sent on your mobile number</p>

                <div className="mb-3">
                    <label className={styles.fieldLabel}>Enter verification code</label>
                    <div className="d-flex justify-content-center">
                        <div className="d-flex" style={{ gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                            {[0, 1, 2, 3, 4, 5].map(index => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength="1"
                                    value={smsVerificationCode[index]}
                                    onChange={event => handleSmsCodeChange(index, event)}
                                    onKeyDown={event => handleSmsKeyDown(index, event)}
                                    ref={ref => (smsInputRefs.current[index] = ref)}
                                    onPaste={event => handleSmsPaste(event)}
                                    className={'form-control text-center ' + styles.inputField}
                                    style={{
                                        width: '14%',
                                        height: '48px',
                                        fontSize: '18px',
                                        fontFamily: 'monospace',
                                        fontWeight: 'bold',
                                        border: 'none',
                                        borderBottom: '1px solid var(--Neutral-Gray-6)',
                                        outline: 'none',
                                        textAlign: 'center',
                                        borderRadius: '0px',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-4">
                    <button onClick={handleSmsActivation} disabled={smsVerificationCode.join('').length !== 6 || registerLoading} className={styles.submitBtn}>
                        Activate account {registerLoading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                    </button>
                    <div className="d-flex flex-column align-items-end">
                        <span role="button" onClick={handleBack} className={styles.secondaryBtnLink}>
                            Back
                        </span>
                        <span role="button" onClick={handleSendSmsCode} className={styles.secondaryBtnLink} style={{ fontSize: '14px', marginTop: '8px' }}>
                            Resend code
                        </span>
                    </div>
                </div>
            </div>
        );
    }
};

export default CompleteProfile;
